import * as React from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { PromoCodeProps, PromoCodeStatus } from '../types';
import Link from '../../Link';
import PromoCodeForm from '../Form/PromoCodeForm';
import { useTheme } from '../../theme';
import * as icons from '../../Icons/';
import { useConfig } from '../../context';
import { useRenderers } from '../../renderProps';
import { ModalX } from '../../BaseComponents';

/**
 * you can get all needed data from ../hooks.ts ( except onSubmit handler )
 */
const PromoCode: React.FC<PromoCodeProps> = props => {
	const { t } = useTranslation('PromoCode');
	const { PromoCode: theme } = useTheme('PromoCode');
	const { renderPromoCodeForm: RenderPromoCodeFormOverride = PromoCodeForm } = useRenderers();
	const {
		global: { allowPromoCodes }
	} = useConfig();

	const SHOW_MODAL_IF_STATUS = [PromoCodeStatus.Empty, PromoCodeStatus.NotApplied];
	const SHOW_CLEAR_IF_STATUS = [PromoCodeStatus.Applied, PromoCodeStatus.Provided, PromoCodeStatus.NotApplied];

	const clearTrigger = props.onClear && (
		<span className={theme.clearWrapper}>
			<Link action={props.onClear} className={theme.clear}>
				{t('reset')} <span className={theme.clearIcon}>{icons.CloseClear}</span>
			</Link>
		</span>
	);

	return allowPromoCodes ? (
		<>
			<div
				className={cn(theme.promoCode, props.className, {
					[theme.promoCode_applied]: props.status === PromoCodeStatus.Applied,
					[theme.promoCode_notApplied]: props.status === PromoCodeStatus.NotApplied,
					[theme.promoCode_empty]: props.status === PromoCodeStatus.Empty
				})}
			>
				<div className={theme.contentWrp}>
					{SHOW_MODAL_IF_STATUS.includes(props.status) && (
						<span className={theme.content}>
							{props.status === PromoCodeStatus.Empty && (
								<Link
									className={cn(theme.trigger, props.promoCodeClassName)}
									action={props.onOpenForm}
									dataTestId="promo-code-trigger"
								>
									{t('Do you have a promo code?')}
								</Link>
							)}

							{props.status === PromoCodeStatus.NotApplied && (
								<Link
									className={theme.trigger}
									action={props.onOpenForm}
									dataTestId="promo-code-trigger"
								>
									<span className={theme.icon_notApplied}>{icons.Warning}</span>
									{t('Promo code not applied')}
								</Link>
							)}
						</span>
					)}

					{SHOW_CLEAR_IF_STATUS.includes(props.status) && (
						<>
							<span className={theme.content}>
								{props.status === PromoCodeStatus.Provided && props.promoCode && (
									<span>
										{t('Promo code')}: {props.promoCode}
									</span>
								)}

								{props.status === PromoCodeStatus.Applied && props.promoCode && (
									<>
										<span className={theme.icon}>{icons.Included}</span>
										{t('Promo code applied')}
									</>
								)}
							</span>
							{props.promoCode && clearTrigger}
						</>
					)}
					{props.renderAfter && props.renderAfter(props)}
				</div>
			</div>

			<ModalX isOpen={props.isFormOpen} onClose={props.onCloseForm} size="small" keepMounted={false}>
				<RenderPromoCodeFormOverride
					isLoading={props.isLoading}
					status={props.status}
					onSubmit={props.onAddPromoCode}
					title={t('Do you have a promo code?')}
				/>
			</ModalX>
		</>
	) : null;
};

export default PromoCode;
